/**
 * Interactive B-spline editor using Plotly.
 *
 * Control points are rendered as draggable Plotly shapes (pixel-sized circles).
 * The evaluated curve is fetched from the backend on every CP / degree change.
 *
 * Props
 * -----
 * degree          number
 * controlPoints   [[x,y], ...]
 * onChange        (newCPs) => void
 * xLabel, yLabel  string
 * color           string (hex)
 * xRange, yRange  [min, max]
 * allLines        [{name, control_points, color}]  — overlay other lines (read-only)
 * activeIdx       number  (-1 = show all as overlays)
 */
import { useEffect, useRef, useCallback, useState } from "react";
import Plot from "react-plotly.js";
import { geometryApi } from "../../api/blades";

export default function SplineEditor({
  degree = 3,
  controlPoints = [],
  onChange,
  xLabel = "x",
  yLabel = "y",
  color = "#3b82f6",
  xRange,
  yRange,
  allLines,
  activeIdx,
}) {
  const [curvePoints, setCurvePoints] = useState([]);
  const [overlayPoints, setOverlayPoints] = useState([]);

  // Incrementing IDs let us discard stale API responses without cleanup functions.
  // Cleanup functions + string-keyed deps break in React 18 StrictMode (first call
  // gets cancelled, second mount skips the effect because deps are unchanged).
  const curveIdRef = useRef(0);
  const overlayIdRef = useRef(0);

  // Stable refs for use inside event callbacks (avoid stale closures)
  const controlPointsRef = useRef(controlPoints);
  const onChangeRef = useRef(onChange);
  const xRangeRef = useRef(xRange);
  useEffect(() => { controlPointsRef.current = controlPoints; }, [controlPoints]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { xRangeRef.current = xRange; }, [xRange]);

  // Preserve zoom / pan across re-renders without triggering extra renders
  const axisRangesRef = useRef({
    xaxis: xRange ? { range: xRange, autorange: false } : {},
    yaxis: yRange ? { range: yRange, autorange: false } : {},
  });

  // ── curve evaluation — keyed on stringified values so React deduplicates ──

  const cpKey = JSON.stringify(controlPoints);

  useEffect(() => {
    if (!controlPoints.length || controlPoints.length < degree + 1) return;
    const id = ++curveIdRef.current;
    geometryApi.evaluateBspline(degree, controlPoints, 120)
      .then(({ data }) => {
        if (id === curveIdRef.current) setCurvePoints(data.curve_points || []);
      })
      .catch(err => {
        console.warn("SplineEditor: curve eval failed",
          err?.response?.status ?? err?.message);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpKey, degree]);

  // ── overlay evaluation ────────────────────────────────────────────

  const overlayKey = JSON.stringify(
    allLines ? allLines.filter((_, i) => i !== activeIdx) : null
  );

  useEffect(() => {
    if (!allLines?.length) return;
    const id = ++overlayIdRef.current;
    Promise.all(
      allLines.map(async (l, i) => {
        const d = l.degree ?? degree;
        if (i === activeIdx || !l.control_points ||
            l.control_points.length < d + 1) return null;
        try {
          const { data } = await geometryApi.evaluateBspline(d, l.control_points, 80);
          return { name: l.name, pts: data.curve_points || [], color: l.color };
        } catch { return null; }
      })
    ).then(results => {
      if (id === overlayIdRef.current) setOverlayPoints(results.filter(Boolean));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayKey, degree]);

  // ── relayout handler: shape drag + zoom/pan preservation ──────────

  const handleRelayout = useCallback((update) => {
    // Preserve zoom / pan
    if (update["xaxis.range[0]"] !== undefined) {
      axisRangesRef.current.xaxis = {
        ...axisRangesRef.current.xaxis,
        range: [update["xaxis.range[0]"], update["xaxis.range[1]"]],
        autorange: false,
      };
    }
    if (update["xaxis.autorange"]) {
      axisRangesRef.current.xaxis = xRangeRef.current
        ? { range: xRangeRef.current, autorange: false }
        : { ...axisRangesRef.current.xaxis, autorange: true, range: undefined };
    }
    if (update["yaxis.range[0]"] !== undefined) {
      axisRangesRef.current.yaxis = {
        ...axisRangesRef.current.yaxis,
        range: [update["yaxis.range[0]"], update["yaxis.range[1]"]],
        autorange: false,
      };
    }
    if (update["yaxis.autorange"]) {
      axisRangesRef.current.yaxis = { ...axisRangesRef.current.yaxis, autorange: true, range: undefined };
    }

    // Detect shape (control-point) drags
    const anchors = {};
    Object.entries(update).forEach(([key, val]) => {
      const m = key.match(/^shapes\[(\d+)\]\.(xanchor|yanchor)$/);
      if (m) {
        const idx = parseInt(m[1]);
        anchors[idx] = anchors[idx] || {};
        anchors[idx][m[2]] = val;
      }
    });
    if (Object.keys(anchors).length > 0) {
      const newCPs = controlPointsRef.current.map((cp, i) => {
        const a = anchors[i];
        return a ? [a.xanchor ?? cp[0], a.yanchor ?? cp[1]] : cp;
      });
      onChangeRef.current?.(newCPs);
    }
  }, []);

  // ── traces ────────────────────────────────────────────────────────

  const cvX = curvePoints.map(p => p[0]);
  const cvY = curvePoints.map(p => p[1]);
  const cpX = controlPoints.map(p => p[0]);
  const cpY = controlPoints.map(p => p[1]);

  const traces = [
    {
      type: "scatter", mode: "lines", name: "Curve",
      x: cvX, y: cvY,
      line: { color, width: 2.5 }, hoverinfo: "skip",
    },
    {
      type: "scatter", mode: "lines", name: "Control polygon",
      x: cpX, y: cpY,
      line: { color: color + "55", width: 1, dash: "dot" }, hoverinfo: "skip",
    },
    ...overlayPoints.map(ov => ({
      type: "scatter", mode: "lines",
      x: ov.pts.map(p => p[0]), y: ov.pts.map(p => p[1]),
      name: ov.name, line: { color: ov.color + "99", width: 1.5 }, hoverinfo: "skip",
    })),
  ];

  // Control points as pixel-sized draggable circle shapes
  // Filter out any NaN/undefined CPs — NaN anchors make Plotly's pick crash
  const shapes = controlPoints.filter(cp => Array.isArray(cp) && !isNaN(cp[0]) && !isNaN(cp[1])).map(cp => ({
    type: "circle",
    xref: "x", yref: "y",
    xsizemode: "pixel", ysizemode: "pixel",
    xanchor: cp[0], yanchor: cp[1],
    x0: -9, x1: 9, y0: -9, y1: 9,
    fillcolor: "white",
    line: { color, width: 2.5 },
  }));

  const layout = {
    margin: { l: 50, r: 20, t: 10, b: 45 },
    xaxis: { title: xLabel, gridcolor: "#e5e7eb", ...axisRangesRef.current.xaxis },
    yaxis: { title: yLabel, gridcolor: "#e5e7eb", ...axisRangesRef.current.yaxis },
    paper_bgcolor: "transparent",
    plot_bgcolor: "#f9fafb",
    showlegend: false,
    dragmode: "pan",
    shapes,
  };

  const config = {
    edits: { shapePosition: true },
    displayModeBar: "hover",
    modeBarButtons: [["zoom2d", "pan2d", "resetScale2d", "toImage"]],
    modeBarButtonsToRemove: ["select2d", "lasso2d", "autoScale2d"],
    scrollZoom: true,
    responsive: true,
    displaylogo: false,
  };

  return (
    <Plot
      data={traces}
      layout={layout}
      config={config}
      onRelayout={handleRelayout}
      style={{ width: "100%", height: "100%" }}
      useResizeHandler
    />
  );
}
